import { passUrl } from '@/lib/api/pass-origin';

/**
 * URLs de l'API v2 du Pass, surface de l'espace perso.
 *
 * Écrites ici et nulle part ailleurs : la v1 publiait ses routes par nom (`links['contracts.get']`),
 * ce qui couplait le front aux noms internes du Pass. La v2 n'a pas de catalogue, volontairement
 * (cf. `pass/docs/API_V2_PERSO.md` §2).
 *
 * Chemins relatifs à `/api/v2/perso` (client `apiV2`), sauf `PUBLIC` (client `apiV2Public`, base
 * `/api/v2`) et `PDF` (URLs absolues, ouvertes par un lien ordinaire : le cookie suit).
 */

type Id = string | number;

export const ENDPOINTS = {
    me: '/me',
    mePassword: '/me/password',

    contracts: '/contracts',
    contract: (contractId: Id) => `/contracts/${contractId}`,
    contractDocuments: (contractId: Id) => `/contracts/${contractId}/documents`,
    contractFractionnement: (contractId: Id) => `/contracts/${contractId}/fractionnement`,
    contractHivernagePreview: (contractId: Id) => `/contracts/${contractId}/hivernage/preview`,
    contractHivernageConfirm: (contractId: Id) => `/contracts/${contractId}/hivernage/confirm`,
    contractHivernageEnd: (contractId: Id) => `/contracts/${contractId}/hivernage/end`,
    contractResiliationPreview: (contractId: Id) => `/contracts/${contractId}/resiliation/preview`,
    contractResiliationRequest: (contractId: Id) => `/contracts/${contractId}/resiliation/request`,
    contractTransitoireTerminate: (contractId: Id) => `/contracts/${contractId}/transitoire/terminate`,
    contractDirectDebits: (contractId: Id) => `/contracts/${contractId}/direct-debits`,
    contractSignatures: (contractId: Id) => `/contracts/${contractId}/signatures`,
    contractSignaturesSend: (contractId: Id) => `/contracts/${contractId}/signatures/send`,
    contractTiersPayeur: (contractId: Id) => `/contracts/${contractId}/tiers-payeur`,
    contractHamon: (contractId: Id) => `/contracts/${contractId}/hamon/resiliation`,
    contractRib: (contractId: Id) => `/contracts/${contractId}/rib`,
    contractAddress: (contractId: Id) => `/contracts/${contractId}/address`,
    contractReceipts: (contractId: Id) => `/contracts/${contractId}/receipts`,
    contractReceipt: (contractId: Id, receiptId: Id) => `/contracts/${contractId}/receipts/${receiptId}`,
    contractReceiptAttestationNvei: (contractId: Id, receiptId: Id) =>
        `/contracts/${contractId}/receipts/${receiptId}/attestation-nvei`,

    fleet: (contractId: Id) => `/contracts/${contractId}/fleet`,
    fleetVehicle: (contractId: Id, adhesion: Id) => `/contracts/${contractId}/fleet/${adhesion}`,
    fleetExit: (contractId: Id) => `/contracts/${contractId}/fleet/exit`,
    fleetAccidents: (contractId: Id) => `/contracts/${contractId}/fleet/accidents`,

    receipts: '/receipts',
    receiptsPaid: '/receipts/paid',

    tiersContact: (tiersId: Id) => `/tiers/${tiersId}/contact`,
    tiersDemat: (tiersId: Id) => `/tiers/${tiersId}/demat`,
    tiersRegulatoryInfos: (tiersId: Id) => `/tiers/${tiersId}/regulatory-infos`,
    tiersCommunicationPreferences: (tiersId: Id) => `/tiers/${tiersId}/communication-preferences`,
    tiersReferrals: (tiersId: Id) => `/tiers/${tiersId}/referrals`,
    tiersAccidents: (tiersId: Id) => `/tiers/${tiersId}/accidents`,

    contractAccidents: (contractId: Id) => `/contracts/${contractId}/accidents`,
    contractAccident: (contractId: Id, accidentId: Id) => `/contracts/${contractId}/accidents/${accidentId}`,
    contractAccidentDocuments: (contractId: Id, accidentId: Id) =>
        `/contracts/${contractId}/accidents/${accidentId}/documents`,
    contractAccidentEligibility: (contractId: Id) => `/contracts/${contractId}/accidents/eligibility`,
    contractAccidentRejection: (contractId: Id) => `/contracts/${contractId}/accidents/rejection`,
    contractNeededAccidentDocuments: (contractId: Id) => `/contracts/${contractId}/accident-documents`,
    contractMobiliteClaim: (contractId: Id) => `/contracts/${contractId}/mobilite-claim`,
    accidentRepairer: (accidentId: Id) => `/accidents/${accidentId}/repairer`,
    accidentCategories: '/accident-categories',
    casIda: '/cas-ida',
    companies: '/companies',
    garages: (postalCode: Id) => `/garages/${postalCode}`,

    booking: (slug: string) => `/booking/${slug}`,
    promoCode: '/promo-code',
    recontact: '/recontact',
    faq: '/faq',
    documentRequest: (type: string) => `/document-requests/${type}`,
    products: '/products',
    productDocuments: '/product-documents',
} as const;

/** Référentiels publics (`/api/v2`), sans authentification. */
export const PUBLIC_ENDPOINTS = {
    contact: '/nous-contacter',
    countries: '/countries',
    /** Création d'accès assuré (« identifiant oublié ») : envoie le lien de définition du mot de passe. */
    customers: '/customers',
    insurers: '/insurers',
    towns: '/towns',
    town: (postalCode: Id) => `/towns/${postalCode}`,
    whitelabel: (whitelabelId: Id) => `/whitelabels/${whitelabelId}`,
    whitelabelByBelair: (belairId: Id) => `/whitelabels/by-belair/${belairId}`,
} as const;

const perso = (path: string) => passUrl(`/api/v2/perso${path}`);

/** Documents PDF : réponses binaires hors enveloppe, à ouvrir par un lien (cf. `APILink`). */
export const PDF = {
    contractAttestation: (contractId: Id) => perso(`/contracts/${contractId}/attestation.pdf`),
    receiptsSchedule: (contractId: Id) => perso(`/contracts/${contractId}/receipts.pdf`),
    releveInformation: (contractId: Id) => perso(`/contracts/${contractId}/ri.pdf`),
    avisEcheance: (contractId: Id) => perso(`/contracts/${contractId}/avis-echeance.pdf`),
    declarationCirconstanciee: (contractId: Id) => perso(`/contracts/${contractId}/declaration-circonstanciee.pdf`),
    vehiclesExport: (contractId: Id) => perso(`/contracts/${contractId}/vehicles.xls`),
    receiptAttestation: (contractId: Id, receiptId: Id) =>
        perso(`/contracts/${contractId}/receipts/${receiptId}/attestation.pdf`),
    receiptMemo: (contractId: Id, receiptId: Id) => perso(`/contracts/${contractId}/receipts/${receiptId}/memo.pdf`),
    receiptRcCircuit: (contractId: Id, receiptId: Id) =>
        perso(`/contracts/${contractId}/receipts/${receiptId}/attestation-rc-circuit.pdf`),
    attestationNonAlcoolemie: (contractId: Id, accidentId: Id) =>
        perso(`/contracts/${contractId}/accidents/${accidentId}/attestation-non-alcoolemie.pdf`),
    fleetVehicleMemo: (vehicleId: Id) => perso(`/fleet/${vehicleId}/memo.pdf`),
} as const;
